import React from 'react';
import type { CalculationResult } from '../types';
import { formatCurrency } from '../utils/formatCurrency';

interface DiscountSummaryProps {
  result: CalculationResult;
  pointsEarned?: number;
  effectiveMultiplier?: number;
  isGuest?: boolean;
}

export const DiscountSummary: React.FC<DiscountSummaryProps> = ({
  result,
  pointsEarned,
  effectiveMultiplier,
  isGuest = false,
}) => {
  const hasShippingDiscount = Boolean(result?.shipping_discount?.eligible);
  if (!result || (result.total_discount === 0 && !hasShippingDiscount)) {
    return null;
  }

  const hasAppliedDiscounts = result.applied_discounts.length > 0;
  const showPointsBlock =
    typeof pointsEarned === 'number' &&
    Number.isFinite(pointsEarned) &&
    typeof effectiveMultiplier === 'number' &&
    Number.isFinite(effectiveMultiplier);
  const normalizedPoints = showPointsBlock ? Math.max(Math.round(pointsEarned as number), 0) : 0;
  const formattedMultiplierLabel = showPointsBlock
    ? (effectiveMultiplier as number).toFixed(2).replace(/\.00$/, '')
    : '';

  return (
    <div className="intan-discount-summary" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e0e0e0' }}>
      {isGuest && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
          <a
            href="https://familysk.sitikhadijah.com/home"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: '#35527d',
              textDecoration: 'none',
              fontWeight: 600,
            }}
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                backgroundColor: '#e6efff',
                color: '#27436d',
                fontSize: '12px',
                fontWeight: 700,
              }}
              aria-hidden="true"
            >
              i
            </span>
            <span>
              Join SK Rewards for{' '}
              <span style={{ fontWeight: 700 }}>
                {showPointsBlock ? `${normalizedPoints} pts` : 'bonus points'}
              </span>
            </span>
          </a>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
        <span>Subtotal:</span>
        <span>{formatCurrency(result.original_total, { withSpace: true })}</span>
      </div>

      {hasAppliedDiscounts && (
        <div style={{ marginBottom: '12px', padding: '8px 0' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#2c5aa0', marginBottom: '6px' }}>
            Savings Applied
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {result.applied_discounts.map((discount) => (
              <div
                key={discount.discount_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '13px',
                  color: '#35527d'
                }}
              >
                <span style={{ fontWeight: 500, flexShrink: 0, minWidth: 0 }}>
                  {discount.discount_title}
                </span>
                <div style={{ color: '#1f7a42', fontWeight: 600, marginLeft: '16px', flexShrink: 0 }}>
                  - {formatCurrency(discount.total_discount, { withSpace: true })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasShippingDiscount && (
        <div style={{ marginBottom: '8px', fontSize: '13px', color: '#35527d', fontWeight: 600 }}>
          Shipping: Free at checkout
        </div>
      )}
      
      {result.total_discount > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: '#28a745' }}>
          <span>Discount:</span>
          <span>- {formatCurrency(result.total_discount, { withSpace: true })}</span>
        </div>
      )}

      {showPointsBlock && !isGuest && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            padding: '10px 12px',
            backgroundColor: '#f2f7ff',
            borderRadius: '8px',
            fontSize: '13px',
            color: '#1e3b70',
            border: '1px solid #d9e6ff',
          }}
        >
          <span style={{ fontWeight: 600 }}>Points Earned:</span>
          <span style={{ fontWeight: 700 }}>
            {`${normalizedPoints} pts`} <span style={{ fontWeight: 500, color: '#4c6eb8' }}>(×{formattedMultiplierLabel})</span>
          </span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 'bold', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
        <span>Total:</span>
        <span>{formatCurrency(result.final_total, { withSpace: true })}</span>
      </div>
    </div>
  );
};

